<script setup>
import { useTemplateRef, computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useConversationFilterPresets } from './presets.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';
import FilterPresets from './FilterPresets.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
  isFilterApplied: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'applyFilter',
  'updateFolder',
  'resetFilters',
  'close',
]);
const { filterTypes } = useConversationFilterContext();
const { presets } = useConversationFilterPresets();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const open = async () => {
  dialogRef.value?.open();
  // The parent seeds the rows from the active view in the same tick it calls
  // open, so the model only lands here after the next render; an empty list
  // would otherwise open a dialog with nothing to edit.
  await nextTick();
  if (!filters.value.length) resetFilter();
  // The dialog stays mounted between openings, so the folder name has to be
  // re-read instead of relying on a fresh setup.
  folderNameLocal.value = props.folderName;
};

const close = () => dialogRef.value?.close();

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const OPERATORS_WITHOUT_VALUE = ['is_present', 'is_not_present'];

const isConditionFilledIn = condition => {
  if (OPERATORS_WITHOUT_VALUE.includes(condition.filterOperator)) return true;
  const { values } = condition;
  if (values === undefined || values === null || values === '') return false;
  return Array.isArray(values) ? values.length > 0 : true;
};

// A shortcut replaces the conditions it actually sets and leaves the rest alone:
// opening the filter inside an inbox seeds an inbox condition, and wiping it on
// every shortcut click silently widened the search. Half-filled rows are dropped
// instead of kept, otherwise the merge would carry an invalid condition along.
const applyPreset = preset => {
  const built = preset.build();
  const replaced = new Set(built.map(condition => condition.attributeKey));
  const kept = filters.value.filter(
    condition =>
      !replaced.has(condition.attributeKey) && isConditionFilledIn(condition)
  );
  filters.value = [...kept, ...built];
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
    close();
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
  close();
}

// "Limpar" only wipes the rows being composed. Once a filter is live, the useful
// action is leaving filter mode altogether, which the row reset never did: the
// list stayed filtered until you clicked away to another view.
const onSecondaryAction = () => {
  if (props.isFilterApplied) {
    emit('resetFilters');
    close();
    return;
  }
  resetFilter();
};

const secondaryButtonLabel = computed(() =>
  props.isFilterApplied
    ? t('FILTER.REMOVE_BUTTON_LABEL')
    : t('FILTER.CLEAR_BUTTON_LABEL')
);

const onConfirm = () => {
  if (props.isFolderView) {
    updateSavedCustomViews();
    return;
  }
  validateAndSubmit();
};

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

const submitButtonLabel = computed(() => {
  return props.isFolderView
    ? t('FILTER.UPDATE_BUTTON_LABEL')
    : t('FILTER.SUBMIT_BUTTON_LABEL');
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="filterModalHeaderTitle"
    @confirm="onConfirm"
    @close="emit('close')"
  >
    <div v-if="props.isFolderView" class="pb-6 border-b border-n-weak">
      <Input
        v-model="folderNameLocal"
        :label="t('FILTER.FOLDER_LABEL')"
        :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
      />
    </div>
    <FilterPresets :presets="presets" @select="applyPreset" />
    <div class="flex flex-col gap-2">
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('FILTER.CONDITIONS_TITLE') }}
      </span>
      <ul class="grid gap-4 list-none">
        <template v-for="(filter, index) in filters" :key="filter.id">
          <ConditionRow
            v-if="index === 0"
            ref="conditionsRef"
            :key="`filter-${filter.attributeKey}-0`"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:values="filter.values"
            :filter-types="filterTypes"
            :show-query-operator="false"
            @remove="removeFilter(index)"
          />
          <ConditionRow
            v-else
            :key="`filter-${filter.attributeKey}-${index}`"
            ref="conditionsRef"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:query-operator="filters[index - 1].queryOperator"
            v-model:values="filter.values"
            show-query-operator
            :filter-types="filterTypes"
            @remove="removeFilter(index)"
          />
        </template>
      </ul>
      <Button
        type="button"
        sm
        ghost
        blue
        class="self-start"
        icon="i-lucide-plus"
        @click="addFilter"
      >
        {{ t('FILTER.ADD_NEW_FILTER') }}
      </Button>
    </div>
    <template #footer>
      <div class="flex gap-2 justify-end">
        <Button
          type="button"
          sm
          faded
          slate
          class="whitespace-nowrap"
          @click="onSecondaryAction"
        >
          {{ secondaryButtonLabel }}
        </Button>
        <Button
          type="submit"
          sm
          solid
          blue
          class="whitespace-nowrap"
          :disabled="isFolderView && !folderNameLocal"
        >
          {{ submitButtonLabel }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>
